// @ts-strict-ignore
import {
  Button, ButtonSize, ButtonVariant, Icon, IconName, Text, Tooltip, useSnackBar,
} from '@usealloy/component-library';
import React, { useCallback, useContext, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import type { Action } from 'flow/store/engine/types';
import {
  getFormAdapter,

} from 'flow/editor/components/NodeEditorDrawer/useNodeEditorForm';
import type { NodeEditorDrawerFooterProps } from 'flow/editor/components/NodeEditorDrawer/useNodeEditorForm';
import {
  selectFocusedAction,
  selectGraphTopology,
  selectWorkflowData,
  selectActiveNodeEditorTab,
  selectWorkflowProps,
} from 'flow/store/redux/selectors';
import { useModalGroup } from 'flow/editor/hooks';

import {
  PublishedAttributesPermissionMessages,
} from '~/dashboard/views/settings/published-attributes/constants';
import { PublishedAttributeOwnerType } from '~/dashboard/views/settings/published-attributes/types';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import { UserContext } from '~/dashboard/context';

import AttributeConfigurationFooterModals from './FooterModals';
import { footerModalsNames } from './FooterModals/constants';
import type { FooterModalsNames } from './FooterModals/types';
import type { AttributeConfigurationFormState } from '../types';
import { StyledFooter, StyledSpacer } from './styles';
import { StyledPublishedAttributeBanner } from '../styles';
import { useEditPublishedAttributeMutation } from '~/dashboard/views/settings/published-attributes/hooks';
import {
  getActionName, getActionTypeText, isActionOfType,
} from 'flow/service/actions';
import { updatePublishedAttributeInProps } from '~/dashboard/views/workflows/store/redux/slice';
import { inputAttributeConfigurationFormAdapter } from '../InputAttributeConfigurationTab/helpers';
import { outputAttributeOptionsFormAdapter } from '../OutputAttributeOptionsTab/helpers';
import { ActionType } from '~/dashboard/views/workflows/store/engine/constants';
import {
  getChangedAttributeConfigurationFieldElementIds,
  mapFormDataToPublishedAttributeProp,
} from '../helpers';
import { createWorkflowHistoryThunk } from 'flow/store/redux/thunks';
import { Operation } from 'flow/service/changelog/types';
import { createUiEffectContext } from 'flow/service/history';
import { changeDescription } from 'flow/store/redux/types';

export default function AttributeConfigurationFooter({
  onCancel,
  onSave,
  saveDisabled = true,
  setIsFormDisabled,
}: NodeEditorDrawerFooterProps<AttributeConfigurationFormState>) {
  const addSnackBar = useSnackBar();
  const dispatch = useAppDispatch();
  const { actions } = useAppSelector(selectWorkflowData);
  const workflowProps = useAppSelector(selectWorkflowProps);
  const focusedAction = useAppSelector(selectFocusedAction);
  const activeNodeEditorTab = useAppSelector(selectActiveNodeEditorTab);
  const formMethods = useFormContext<AttributeConfigurationFormState>();
  const modalGroup = useModalGroup<FooterModalsNames>();
  const graphTopology = useAppSelector(selectGraphTopology);
  const { permissions, featureFlags } = useContext(UserContext);

  const agentCanUpdatePublishedAttributes = Boolean(permissions['UPDATE:PublishedAttributes:null']);
  const focusedActionDependents = React.useRef<Action[]>([]);
  const publishedAttributeToken = formMethods.watch('attributeToken');
  const isPublishedAttribute = Boolean(publishedAttributeToken);
  const isSystemPublishedAttribute = isPublishedAttribute
    && (workflowProps?.publishedAttributes ?? []).some(
      pa => pa.attributeToken === publishedAttributeToken
        && pa.ownerType === PublishedAttributeOwnerType.SYSTEM,
    );
  // System PAs are managed by Alloy and cannot be edited from the workflow editor.
  const canEditPublishedAttribute = agentCanUpdatePublishedAttributes
    && !isSystemPublishedAttribute;
  const isInputAttributeAction = isActionOfType(focusedAction, ActionType.INPUT);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    mutateAsync: editPublishAttributeMutation,
  } = useEditPublishedAttributeMutation();

  const updatePublishedAttributeRemote = useCallback(async (
    formData: AttributeConfigurationFormState,
  ) => {
    try {
      await editPublishAttributeMutation({
        ...formData,
        dataType: formData.dataType.value,
      });

      setIsLoading(false);
      onSave(formData);
    } catch (err) {
      setIsLoading(false);
      addSnackBar({
        title: `Could not update ${getActionTypeText(focusedAction)} ${getActionName(focusedAction)}`,
        type: 'danger',
        duration: 2_000,
      });
      setIsFormDisabled(false);
    }
  }, [
    addSnackBar,
    editPublishAttributeMutation,
    focusedAction,
    onSave,
    setIsFormDisabled,
  ]);
  /**
 * This form + redux handles updating the input/output action and the connected published attribute
 * We keep this data separate since WF Actions + PA saving is handled differently
 */
  const updatePublishedAttributeLocal = useCallback((
    formData: AttributeConfigurationFormState,
  ) => {
    // map forms to action
    const adapter = getFormAdapter(focusedAction, activeNodeEditorTab);
    const updatedAction = adapter.mapFormDataToAction(formData, focusedAction);
    // Transform form data to PA props, converting empty strings to null
    const transformedPAData = mapFormDataToPublishedAttributeProp(formData);
    const [htmlElementId, ...relatedHtmlElementIds] = (
      getChangedAttributeConfigurationFieldElementIds(
        isInputAttributeAction ? 'input--configuration' : 'output--options',
        formData,
        (isInputAttributeAction
          ? inputAttributeConfigurationFormAdapter
          : outputAttributeOptionsFormAdapter
        ).getDefaultFormValues(focusedAction, workflowProps),
      )
    );

    const stateChange = () => dispatch(updatePublishedAttributeInProps({
      updatedPublishedAttributeData: transformedPAData,
      updatedActionData: updatedAction,
      actionId: focusedAction.publicId,
    }));
    // all PA edits are applied at once, so they belong in a single history commit
    dispatch(createWorkflowHistoryThunk(
      stateChange,
      Boolean(featureFlags?.workflowEditorUndoRedo),
      {
        operation: Operation.Modify,
        uiEffectContext: createUiEffectContext(
          isInputAttributeAction
            ? changeDescription.inputConfiguration
            : changeDescription.outputOptions,
          {
            actionId: focusedAction.publicId,
            ...(htmlElementId && { htmlElementId }),
            ...(relatedHtmlElementIds.length && { relatedHtmlElementIds }),
          },
        ),
      },
    ));
    setIsLoading(false);
    onSave(formData);
  }, [
    dispatch,
    onSave,
    focusedAction,
    activeNodeEditorTab,
    featureFlags?.workflowEditorUndoRedo,
    isInputAttributeAction,
    workflowProps,
  ]);

  // Wrapped handler that triggers validation before submit
  // handleSubmit will pass the validated form data as the first parameter
  const handleValidSubmit = useCallback(async (formData: AttributeConfigurationFormState) => {
    setIsFormDisabled(true);
    setIsLoading(true);

    // System PAs are Alloy-managed; never issue a PA mutation (PATCH or staged update) for them.
    if (isSystemPublishedAttribute) {
      setIsLoading(false);
      setIsFormDisabled(false);
      onSave(formData);
      return;
    }

    if (featureFlags.useTyphonWorkflowEndpoints) {
      // This will handle both ephemeral and non-ephemeral PAs
      updatePublishedAttributeLocal(formData);
    } else {
      await updatePublishedAttributeRemote(formData);
    }
  }, [
    featureFlags.useTyphonWorkflowEndpoints,
    isSystemPublishedAttribute,
    onSave,
    setIsFormDisabled,
    updatePublishedAttributeLocal,
    updatePublishedAttributeRemote,
  ]);

  // Handler for validation errors
  const handleInvalidSubmit = useCallback(() => {
    setIsFormDisabled(false);
    setIsLoading(false);
  }, [setIsFormDisabled]);

  const handleOnSaveClick = React.useCallback(() => {
    if (formMethods.getFieldState('dataType').isDirty) {
      focusedActionDependents.current = (
        focusedAction
          ? Array.from(
            graphTopology.outgoers.get(focusedAction.publicId) || [],
          ).map(childId => actions[childId])
          : []
      );

      if (focusedActionDependents.current.length) {
        modalGroup.open('confirmDataTypeChange');
      } else {
        // Use default save method (non-PA) - dataType changes are for non-published attributes
        formMethods.handleSubmit(onSave)();
      }
    } else {
      // PA save flow
      formMethods.handleSubmit(handleValidSubmit, handleInvalidSubmit)();
    }
  }, [
    actions,
    focusedAction,
    focusedActionDependents,
    graphTopology.outgoers,
    formMethods,
    modalGroup,
    onSave,
    handleValidSubmit,
    handleInvalidSubmit,
  ]);

  const getPublishedAttributeBannerText = () => {
    if (isSystemPublishedAttribute) {
      return 'This is a system published attribute managed by Alloy. It can be used in decisioning but cannot be edited here.';
    }
    return featureFlags.useTyphonWorkflowEndpoints
      ? 'This attribute is published. Any changes made to the Published Attribute will not take effect until the Workflow is saved, and will impact all Workflows where the attribute is used.'
      : 'This attribute is published. Any changes made to the Published Attribute will impact all Workflows where the attribute is used.';
  };

  return (
    <StyledFooter gap={2}>
      {isPublishedAttribute && (
        <>
          <StyledPublishedAttributeBanner>
            <Icon icon={IconName.Exclamation} />
            <Text>
              {getPublishedAttributeBannerText()}
            </Text>
          </StyledPublishedAttributeBanner>
          <StyledSpacer />
          <Button
            data-dd-action-name="attribute-config-cancel"
            onClick={() => {
              if (focusedAction) {
                // Reset form to default values before closing (only on cancel, not on drawer close)
                const defaultValues = isInputAttributeAction
                  ? inputAttributeConfigurationFormAdapter.getDefaultFormValues(
                    focusedAction,
                    workflowProps,
                  )
                  : outputAttributeOptionsFormAdapter.getDefaultFormValues(
                    focusedAction,
                    workflowProps,
                  );
                formMethods.reset(defaultValues);
              }
              formMethods.clearErrors();
              onCancel();
            }}
            size={ButtonSize.small}
            variant={ButtonVariant.secondary}
          >
            Cancel
          </Button>
          <Tooltip
            content={isSystemPublishedAttribute
              ? PublishedAttributesPermissionMessages.CANNOT_EDIT_SYSTEM
              : PublishedAttributesPermissionMessages.CANNOT_EDIT}
            hide={!isPublishedAttribute || canEditPublishedAttribute}
            side="top"
          >
            <Button
              data-dd-action-name="attribute-config-save"
              disabled={saveDisabled
                || (isPublishedAttribute && !canEditPublishedAttribute)}
              loading={isLoading}
              onClick={handleOnSaveClick}
              size={ButtonSize.small}
              variant={ButtonVariant.primary}
            >
              {featureFlags.useTyphonWorkflowEndpoints ? 'Apply' : 'Save'}
            </Button>
          </Tooltip>
        </>
      )}
      {!isPublishedAttribute && (
        <>
          <AttributeConfigurationFooterModals
            extraProps={{
              [footerModalsNames.confirmDataTypeChange]: {
                onSave,
                focusedAction,
                focusedActionDependents: focusedActionDependents.current,
              },
            }}
            modalGroup={modalGroup}
          />
          <Button
            data-dd-action-name="button-replace-with-published-attribute"
            variant={ButtonVariant.tertiary}
            size={ButtonSize.small}
            style={{ cursor: 'pointer', marginRight: 'var(--cl-space-6)' }}
            onClick={() => modalGroup.open('replaceWithPublishedAttribute')}
          >
            Replace with Published Attribute
          </Button>
        </>
      )}
    </StyledFooter>
  );
}
